import * as React from 'react';
import { ClickAwayListener, Fade, InputLabel, Popper, PopperPlacementType, SxProps, Theme } from '@mui/material';
import { Button } from '../Button';
import { IconKey } from '../Icon';
import { IconButton } from '../IconButton';
import { LayoutGrid } from '../LayoutGrid';
import { List } from '../List';
import { ListItem } from '../ListItem';
import { Paper } from '../Paper';
import { Typography } from '../Typography';
import { keygen } from '../../utils';

/* Please note:  this component currently is built using an outdated approach.
If you want to pattern a new component after previous work, the use the ComboButton */

export interface ContextualMenuPopperTrigger {
  text?: string;
  showText?: boolean;
  icon?: IconKey;
  triggerSelector?: string;
  popperPosition?: 'bottom-start' | 'bottom-end';
}

export interface ContextualMenuPopperMenuItem {
  value: any;
  itemAction?: any;
  disabled?: boolean;
  dataProp?: string;
}

export interface ContextualMenuPopperProps {
  trigger: ContextualMenuPopperTrigger;
  translate: any;
  menuItems: ContextualMenuPopperMenuItem[];
  sxButton?: SxProps<Theme>;
  sxPopper?: SxProps<Theme>;
}

const styles: Record<string, SxProps<Theme>> = {
  menuListWrapper: {
    padding: 0,
    boxShadow: 2
  },
  triggerButton: {
    padding: 1
  },
  listRoot: {
    display: 'flex',
    flexDirection: 'column',
    padding: 0
  },
  listItem: {
    justifyContent: 'flex-start',
    width: 'auto',
    padding: 1
  },
  triggerPadding: {
    '&>p ': {
      paddingRight: 0.5
    },
    '&>svg': {
      paddingLeft: 0.5
    }
  },
  popperContainer: {
    zIndex: 'modal'
  }
};

export const findFirstFocusable = (childNodes: HTMLCollection): number => {
  let firstFocusIndex = 0;
  let i = 0;
  while (i < childNodes.length) {
    if (childNodes[i].getAttribute('tabindex') === '0') {
      firstFocusIndex = i;
      break;
    } else {
      i += 1;
    }
  }
  return firstFocusIndex;
};

export const findLastFocusable = (childNodes: HTMLCollection): number => {
  let lastFocusIndex = 0;
  let i = childNodes.length - 1;
  while (i >= 0) {
    if (childNodes[i].getAttribute('tabindex') === '0') {
      lastFocusIndex = i;
      break;
    } else {
      i -= 1;
    }
  }
  return lastFocusIndex;
};

const keyListeners = {
  tab: 9,
  esc: 27,
  etr: 13,
  spc: 32
};

export const keyDownOpenClose = (
  e: React.KeyboardEvent,
  atEnd: boolean,
  atBeginning: boolean,
  buttonRef?: React.RefObject<HTMLButtonElement>
): boolean => {
  const { tab, esc } = keyListeners;
  const { keyCode, shiftKey } = e;
  let isOpen = true;
  if (shiftKey && keyCode === tab && atBeginning) {
    isOpen = false;
  }
  if (!shiftKey && keyCode === tab && atEnd) {
    isOpen = false;
  }
  if (keyCode === esc) {
    isOpen = false;
  }
  if (!isOpen && buttonRef && buttonRef.current) {
    buttonRef.current.focus();
  }
  return isOpen;
};

const ContextualMenuPopperBase = ({
  trigger: { text, showText, icon, triggerSelector, popperPosition = 'bottom-end' },
  menuItems,
  translate,
  sxButton,
  sxPopper
}: ContextualMenuPopperProps) => {
  const { listRoot, listItem, menuListWrapper, popperContainer } = styles;
  const [anchorEl, setAnchorEl] = React.useState<Element | null>(null);
  const [open, setOpen] = React.useState<boolean>(false);
  const [fromKeyboard, setFromKeyBoard] = React.useState<boolean>(false);
  const [keyPressed, setKeyPressed] = React.useState<number>(0);
  const [placement, setPlacement] = React.useState<PopperPlacementType>();
  const [childNodes, setChildNodes] = React.useState<any>(null);
  const [node, setNode] = React.useState<HTMLUListElement | null>();
  const [firstFocusable, setFirstFocusable] = React.useState<number>(0);
  const [lastFocusable, setLastFocusable] = React.useState<number>(1);
  const defaultText = translate('cmmn:open');
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const buttonId = keygen();

  const handleClick = (newPlacement: PopperPlacementType) => (event: React.MouseEvent<HTMLElement>) => {
    if (keyPressed === keyListeners.etr) {
      return;
    }
    setFromKeyBoard(false);
    setElmPosition(newPlacement, event);
  };

  const setElmPosition = (newPlacement: PopperPlacementType, event: React.MouseEvent<HTMLElement> | React.KeyboardEvent) => {
    setAnchorEl(event.currentTarget);
    setOpen(prev => placement !== newPlacement || !prev);
    setPlacement(newPlacement);
  };

  const handleBtnKeyDown = (newPlacement: PopperPlacementType) => (event: React.KeyboardEvent) => {
    const { etr, spc } = keyListeners;
    const { keyCode } = event;
    setKeyPressed(keyCode);
    const displayMenu = keyCode === etr || keyCode === spc;
    setFromKeyBoard(displayMenu);
    displayMenu && setElmPosition(newPlacement, event);
  };

  const handleClickAway = () => setOpen(false);
  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    const atEnd = index === lastFocusable;
    const atBeginning = index === firstFocusable;
    setOpen(keyDownOpenClose(e, atEnd, atBeginning, buttonRef));
  };
  const handleBlur = (e: React.FocusEvent) => {
    e.preventDefault();
  };
  const menuListRef = React.useCallback(value => {
    setNode(value);
  }, []);

  React.useEffect(() => {
    if (node) {
      setChildNodes(node.children);
    }
  }, [node]);

  React.useEffect(() => {
    if (childNodes) {
      setFirstFocusable(findFirstFocusable(childNodes));
      setLastFocusable(findLastFocusable(childNodes));
      fromKeyboard && childNodes[firstFocusable].focus();
    }
  }, [childNodes, fromKeyboard]);

  return (
    <React.Fragment>
      <LayoutGrid container direction="row" justify="center" alignItems="center">
        <ClickAwayListener onClickAway={React.useCallback(handleClickAway, [])}>
          {icon && !showText ? (
            <IconButton
              icon={icon}
              label={text || defaultText}
              title={text || defaultText}
              onClick={handleClick(popperPosition)}
              onKeyDown={handleBtnKeyDown(popperPosition)}
              dataProp={triggerSelector}
              id={buttonId}
              ref={buttonRef}
              sx={sxButton}
            />
          ) : (
            <Button
              onClick={handleClick(popperPosition)}
              onKeyDown={handleBtnKeyDown(popperPosition)}
              aria-label={text || defaultText}
              label={text || defaultText}
              icon={icon}
              dataProp={triggerSelector}
              id={buttonId}
              ref={buttonRef}
              title={text || defaultText}
              sx={sxButton}
            >
              {text || defaultText}
            </Button>
          )}
        </ClickAwayListener>
        <InputLabel htmlFor={buttonId} aria-label={text || defaultText} />
      </LayoutGrid>
      <Popper
        open={open}
        anchorEl={anchorEl}
        placement={placement}
        transition
        disablePortal
        role="navigation"
        sx={[popperContainer, sxPopper] as SxProps<Theme>}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={350}>
            <Paper elevation={2} sx={menuListWrapper}>
              <List sx={listRoot} ref={menuListRef} role="list">
                {menuItems.map(({ value, itemAction, disabled, dataProp }: ContextualMenuPopperMenuItem, index: number) => {
                  const key = `CMWKey:${index}`;
                  return (
                    <ListItem
                      button
                      alignItems="center"
                      aria-label={value}
                      onClick={itemAction}
                      key={key}
                      sx={listItem}
                      disabled={disabled}
                      onBlur={handleBlur}
                      component="li"
                      onKeyDown={(e: React.KeyboardEvent) => handleKeyDown(e, index)}
                      role="listitem"
                      data-selector={dataProp}
                    >
                      <Typography variant="body1">{value}</Typography>
                    </ListItem>
                  );
                })}
              </List>
            </Paper>
          </Fade>
        )}
      </Popper>
    </React.Fragment>
  );
};

const ContextualMenuPopper = React.memo(ContextualMenuPopperBase);

ContextualMenuPopper.displayName = 'ContextualMenuPopper';

export { ContextualMenuPopper };
